import type { PassGroup } from "../app/freeze-judge.js";
import { CLIP_DELETED, shortHash, type JRow, type Panel, type Part, type Stage } from "../app/judge-model.js";
import type { RunPanel, SchedPanel } from "../app/judge-sched.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { keepTogether } from "../lib/keep.js";

/**
 * Judge mode's views (UX spec v3 §6.14, mock-up 12-judge.html). A different
 * register on purpose, and everything judge-only sits under a `.judge` root — the
 * one root the customer-copy audit exempts. Same palette, no third colour; the
 * outlined Judge tag (the Bank tag is filled), 10 px panels, dense rows, mono
 * figures; never a bank card, never a bank tag, never a primary button (ghost and
 * link only), never a gradient — and nothing here moves money.
 *
 * Every id and hash is a chip: short on screen (10 … 6), the full value in its
 * `aria-label`; a tap shows it in full and copies it, and "Copied" is announced.
 */
const COPIED_MS = 1500;

export function judgeTag(): HTMLElement {
  return el("span", { class: "judgetag" }, ["Judge"]);
}

/** A live region per judge view, for "Copied". */
export function announcer(): HTMLElement {
  return el("span", { class: "vis-hidden", aria: { live: "polite" } });
}

export function hashChip(full: string, label: string, live: HTMLElement, short?: string): HTMLButtonElement {
  const shown = short ?? shortHash(full);
  const code = el("code", {}, [shown]);
  const glyph = icon("i-copy");
  const chip = el("button", { class: "raw hash", type: "button", aria: { expanded: "false", label: `${label}: ${full}. Tap to show in full and copy` } }, [
    code,
    glyph,
  ]);
  chip.dataset["full"] = full;
  chip.addEventListener("click", (event) => {
    event.stopPropagation();
    const open = chip.getAttribute("aria-expanded") !== "true";
    chip.setAttribute("aria-expanded", open ? "true" : "false");
    code.textContent = open ? full : shown;
    void navigator.clipboard?.writeText(full).catch(() => undefined);
    chip.dataset["done"] = "true";
    glyph.querySelector("use")?.setAttribute("href", "#i-check");
    live.textContent = "Copied";
    setTimeout(() => {
      chip.dataset["done"] = "false";
      glyph.querySelector("use")?.setAttribute("href", "#i-copy");
    }, COPIED_MS);
  });
  return chip;
}

function part(p: Part, live: HTMLElement): (string | Node)[] {
  switch (p.kind) {
    case "text":
      return judgeText(p.t);
    case "mono":
      return [el("span", { class: "mono" }, [p.t])];
    case "id":
      return [el("span", { class: "mono id" }, [p.t])];
    case "pass":
      return [el("span", { class: "pass" }, [p.t])];
    case "fail":
      return [el("span", { class: "fail" }, [p.t])];
    case "hash":
      return [hashChip(p.full, p.label, live, p.short)];
  }
}

export function jrows(rows: readonly JRow[], live: HTMLElement): HTMLElement[] {
  return rows.map((r) =>
    el("div", { class: "jrow" }, [
      el("span", { class: "k" }, [r.k]),
      el("span", { class: "v" }, [
        ...r.v.flatMap((p) => part(p, live)),
        ...(r.small === undefined ? [] : [el("small", {}, judgeText(r.small))]),
      ]),
    ]),
  );
}

/** What a judge line keeps whole: a rule id ("WARN-SCAM-CHECK": capitals and digits joined by hyphens), or a separator " ·". */
const KEEP = /\b[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)+\b| ·/g;

/**
 * P1-8 (DCTA-192, polish audit): a judge line of words, as drawn — a rule id in it stays whole (the audit saw "WARN-SCAM- /
 * CHECK"), and a separator never starts a line: " ·" is kept whole with the space before it, so the line breaks after the
 * dot, never before it. Each in a `.nw` span; the text, its spaces and what a screen reader hears are exactly as given.
 */
const judgeText = (t: string): (string | HTMLElement)[] => keepTogether(t, KEEP);

function panel(head: readonly (Node | string)[], body: readonly HTMLElement[], tagged = true): HTMLElement {
  return el("div", { class: "jp" }, [el("div", { class: "jh" }, [...(tagged ? [judgeTag()] : []), ...head]), ...body]);
}

function go(title: string, sub: string, right: Node | undefined, onSelect: () => void): HTMLButtonElement {
  const b = el("button", { class: "raw jrow go", type: "button" }, [
    el("span", { class: "v" }, [el("b", {}, [title]), el("small", {}, [sub])]),
    ...(right === undefined ? [] : [el("span", { class: "r" }, [right])]),
    icon("i-chev-r", "chev"),
  ]);
  b.addEventListener("click", onSelect);
  return b;
}

/** A light question inside a judge panel, for the two demo actions that cannot be undone. */
function ask(acts: HTMLElement, question: string, yes: string, no: string, onYes: () => void, restore: () => void): void {
  const y = el("button", { class: "raw btn ghost", type: "button" }, [yes]);
  const n = el("button", { class: "raw btn link", type: "button" }, [no]);
  y.addEventListener("click", onYes);
  n.addEventListener("click", restore);
  acts.replaceChildren(el("div", { class: "jrow note" }, [el("span", { class: "v" }, [question])]), y, n);
  y.focus({ preventScroll: true });
}

// ------------------------------------------------------------------- 12 on
export interface JudgeHomeModel {
  readonly last?: { readonly id: string; readonly steps: number; readonly signedAt: string; readonly chain?: boolean };
  /** Counts from `tally()`: only an attack that ran is blocked or not (QA-013). */
  readonly attacks?: { readonly blocked: number; readonly ran: number; readonly total: number };
  readonly chain?: { readonly ok: boolean; readonly total: string };
  readonly models: readonly JRow[];
  readonly clock: { readonly offsetSeconds: number; readonly simulated: boolean; readonly now?: string };
  /** DCTA-188 Change 4: the faulty-drafter demo switch — only when the bank's demo routes answer (`/v1/demo/state`). */
  readonly demoFault?: { readonly on: boolean };
  /** DCTA-209 (F62): the Scheduled payments panel, and one "Due payments run" panel per press, newest first. */
  readonly scheduled?: { readonly panel: SchedPanel; readonly runs: readonly RunPanel[] };
}

export interface JudgeHomeHandlers {
  onProof(): void;
  /** DCTA-176 (F49): the identity check's Proof — offered only when this session has one. */
  onIdentity?(): void;
  onAttacks(): void;
  onChain(): void;
  onFastForward(done: (error?: string) => void): void;
  /** DCTA-188 Change 4: the switch flipped (on: the next message's draft is altered on purpose). */
  onDemoFault?(on: boolean): void;
  /** DCTA-209 (F62): "Run them" — route 4; `done(error)` when it did not run. */
  onRunDue?(done: (error?: string) => void): void;
}

/**
 * DCTA-209 (F62, 31a–31c): the Scheduled payments panel — how many are active, the next due, one row per schedule with its
 * mdt· chip — and "Run due payments now", whose ask replaces it in the panel, as the clock's does (built `ask()`).
 */
function schedView(p: SchedPanel, live: HTMLElement, onRunDue: ((done: (error?: string) => void) => void) | undefined): HTMLElement {
  const acts = el("div", { class: "acts jacts" });
  const button = (): void => {
    const b = el("button", { class: "raw btn link rundue", type: "button" }, ["Run due payments now · moves the clock to the next due date"]);
    b.addEventListener("click", () =>
      ask(
        acts,
        "Moves this demo's clock to the next payment's due time, for good, then runs every payment that is due. Every record after it is marked simulated.",
        "Run them",
        "Keep real time",
        () =>
          onRunDue?.((error) => {
            if (error !== undefined) acts.replaceChildren(el("div", { class: "jrow note" }, [el("span", { class: "v" }, [error])]));
          }),
        button,
      ),
    );
    acts.replaceChildren(b);
  };
  if (p.active > 0 && onRunDue !== undefined) button();
  return panel(["Scheduled payments", el("span", { class: "mono" }, [`${String(p.active)} active`])], [...jrows(p.rows, live), ...(acts.childElementCount > 0 ? [acts] : [])]);
}

/** DCTA-209 (31d): one press — the bank time it moved to, then one numbered stage per payment with its record. */
function runView(r: RunPanel, live: HTMLElement): HTMLElement {
  const stages =
    r.stages.length === 0
      ? jrows([{ k: "Ran", v: [{ kind: "text", t: r.empty ?? "" }] }], live)
      : r.stages.map((s) =>
          el("div", { class: "jstage" }, [
            el("div", { class: "sh" }, [el("span", { class: "n" }, [String(s.n)]), s.title, el("span", { class: `mono ${s.state === "executed" ? "pass" : "fail"}` }, [s.state])]),
            ...jrows(s.rows, live),
          ]),
        );
  return panel(["Due payments run", el("span", { class: "mono" }, [r.at])], stages);
}

/**
 * DCTA-188 Change 4 (beat 5): judge mode's one-shot "Faulty drafter (demo)" switch — the next message only, then off by
 * itself (app/demo-fault.ts). A `role="switch"` row in the judge register, the design system's `.sw` knob.
 */
function demoFaultSwitch(on: boolean, flip: (on: boolean) => void): HTMLButtonElement {
  const row = el("button", { class: "raw jrow go swrow", type: "button", role: "switch", aria: { checked: on ? "true" : "false" } }, [
    el("span", { class: "v" }, [
      el("b", {}, ["Faulty drafter (demo)"]),
      el("small", {}, ["The next message's draft is altered on purpose (two amounts swapped). Nothing it produces can be signed."]),
    ]),
    el("span", { class: "sw", aria: { hidden: "true" } }),
  ]);
  row.addEventListener("click", () => {
    const next = row.getAttribute("aria-checked") !== "true";
    row.setAttribute("aria-checked", next ? "true" : "false");
    flip(next);
  });
  return row;
}

export function judgeHome(m: JudgeHomeModel, h: JudgeHomeHandlers): HTMLElement {
  const live = announcer();
  const last = m.last;
  const acts = el("div", { class: "acts" });
  const forward = (): void => {
    const b = el("button", { class: "raw btn link", type: "button" }, ["Fast-forward 12 h · marks records as simulated"]);
    b.addEventListener("click", () =>
      ask(
        acts,
        "Moves the bank's clock 12 hours ahead for everyone on this demo, for good: held payments that come due go out, and every record after it is marked simulated.",
        "Move the clock",
        "Keep real time",
        () =>
          h.onFastForward((error) => {
            if (error !== undefined) acts.replaceChildren(el("div", { class: "jrow note" }, [el("span", { class: "v" }, [error])]));
          }),
        forward,
      ),
    );
    acts.replaceChildren(b);
  };
  forward();
  return el("div", { class: "judge home" }, [
    panel(
      ["Judge mode is on"],
      [
        ...jrows(
          [
            { k: "Power", v: [{ kind: "text", t: "none over money · every screen, signature and rule still applies · " }, { kind: "mono", t: "DEMO_MODE=1" }, { kind: "text", t: " only" }] },
            last === undefined
              ? { k: "Last bundle", v: [{ kind: "text", t: "none signed yet — sign one in the chat" }] }
              : {
                  k: "Last bundle",
                  v: [{ kind: "mono", t: last.id }, { kind: "text", t: ` · ${String(last.steps)} ${last.steps === 1 ? "step" : "steps"} · signed once ${last.signedAt}` }],
                  ...(last.chain === undefined ? {} : { small: last.chain ? "chain ✓" : "chain ✗" }),
                },
          ],
          live,
        ),
        ...(last === undefined
          ? []
          : [go("Proof of the last bundle", "said → drafted → validated → decided → payload → signed → verified → executed → recorded", undefined, h.onProof)]),
        ...(h.onIdentity === undefined
          ? []
          : [go("Proof of the identity check", "turn refused: eligibility before any question → refresh: consent → shown → signed → verified → kyc_refreshed", undefined, h.onIdentity)]),
        go(
          "Attacks",
          "E4 · E5 · replayed signature · a signature for other bytes · expected beside observed",
          m.attacks === undefined ? undefined : attackCount(m.attacks),
          h.onAttacks,
        ),
        go(
          "Chain",
          "GET /v1/audit/verify · replayed from the newest signed checkpoint",
          m.chain === undefined ? undefined : el("span", { class: m.chain.ok ? "pass" : "fail" }, [`${m.chain.total} ${m.chain.ok ? "✓" : "✗"}`]),
          h.onChain,
        ),
        ...(m.demoFault === undefined || h.onDemoFault === undefined ? [] : [demoFaultSwitch(m.demoFault.on, (on) => h.onDemoFault?.(on))]),
      ],
    ),
    panel(["Models and timing", el("span", { class: "mono" }, ["last bundle"])], jrows(m.models, live), false),
    // DCTA-209 (F62): the scheduler — the panel, then each press's run, newest first, right above the Clock they moved.
    ...(m.scheduled === undefined ? [] : [schedView(m.scheduled.panel, live, h.onRunDue), ...m.scheduled.runs.map((r) => runView(r, live))]),
    panel(
      ["Clock", el("span", { class: "mono" }, [m.clock.offsetSeconds === 0 ? "real time · offset 0 s" : `offset ${String(m.clock.offsetSeconds)} s`])],
      [
        ...jrows(
          [
            {
              k: "Simulated time",
              v: [{ kind: "text", t: m.clock.simulated ? "on · every record carries simulated_time: true" : "off · when on, every record carries simulated_time: true" }],
              ...(m.clock.now === undefined ? {} : { small: `bank time ${m.clock.now}` }),
            },
          ],
          live,
        ),
        acts,
      ],
      false,
    ),
    live,
  ]);
}

// ----------------------------------------------------------- pushed screens
export function judgeTopbar(title: string, onBack: () => void, backLabel = "Back to Judge mode"): HTMLElement {
  const back = el("button", { class: "raw icobtn back", type: "button", aria: { label: backLabel } }, [icon("i-chev-l")]);
  back.addEventListener("click", onBack);
  return el("div", { class: "topbar jd" }, [back, el("span", { class: "t jt" }, [title]), el("span", { class: "judge tagslot" }, [judgeTag()])]);
}

/** 12 proof: the one numbered list in the product, because it is a real sequence. */
/** DCTA-198: a kept clip's bytes as the bank stores them and this browser's SHA-256 of them; `undefined` once deleted. */
export type ClipFetch = (audioId: string) => Promise<{ readonly bytes: Uint8Array; readonly hex: string } | undefined>;

/**
 * DCTA-198 (docs/specs/fe-audio-evidence-dcta191.md Change 2): "Play the clip" — the bytes are fetched only on the tap (each
 * read is audited at the bank), played from a blob URL in this browser and re-hashed here against the bank's hash at upload.
 * A clip gone (404) says "deleted", never an error.
 */
function playRow(play: NonNullable<JRow["play"]>, onClip: ClipFetch | undefined, live: HTMLElement): HTMLElement[] {
  const slot = el("div", { class: "jclip" });
  const button = el("button", { class: "raw jrow play", type: "button", aria: { label: "Play the clip", busy: "false" } }, [
    el("span", { class: "k" }, ["Play"]),
    el("span", { class: "v" }, ["Play the clip"]),
  ]);
  let url: string | undefined;
  const said = (v: readonly Part[]): void => void slot.replaceChildren(...jrows([{ k: "Re-hash in this browser", v }], live));
  button.addEventListener("click", () => {
    if (onClip === undefined || button.getAttribute("aria-busy") === "true") return;
    button.setAttribute("aria-busy", "true");
    void onClip(play.audioId).then(
      (got) => {
        button.setAttribute("aria-busy", "false");
        if (url !== undefined) URL.revokeObjectURL(url);
        url = undefined;
        if (got === undefined) {
          said([{ kind: "text", t: CLIP_DELETED }]);
          return;
        }
        url = URL.createObjectURL(new Blob([got.bytes as BlobPart], { type: "audio/wav" }));
        const audio = el("audio", { class: "jaudio" });
        audio.setAttribute("controls", "");
        audio.setAttribute("src", url);
        said([got.hex === play.sha256 ? { kind: "pass", t: "✓ matches" } : { kind: "fail", t: "✗ differs" }, { kind: "text", t: " · " }, { kind: "hash", full: got.hex, label: "this browser's sha256" }]);
        slot.prepend(audio);
      },
      () => {
        button.setAttribute("aria-busy", "false");
        said([{ kind: "text", t: "no answer from the bank" }]);
      },
    );
  });
  return [button, slot];
}

export function proofView(bundleId: string, stages: readonly Stage[], canonicalText: string | undefined, onChain: () => void, onClip?: ClipFetch): HTMLElement {
  const live = announcer();
  const blocks = stages.map((s) => {
    const rows = s.rows.flatMap((r) => (r.play === undefined ? jrows([r], live) : playRow(r.play, onClip, live)));
    if (s.n === 5 && canonicalText !== undefined) {
      const pre = el("pre", { class: "canon", hidden: true }, [canonicalText]);
      const toggle = el("button", { class: "raw jrow bytes", type: "button", aria: { expanded: "false" } }, [
        el("span", { class: "k" }, ["Bytes"]),
        el("span", { class: "v" }, ["Show the canonical bytes"]),
        icon("i-chev-d", "chev"),
      ]);
      toggle.addEventListener("click", () => {
        const open = toggle.getAttribute("aria-expanded") !== "true";
        toggle.setAttribute("aria-expanded", open ? "true" : "false");
        (toggle.querySelector(".v") as HTMLElement).textContent = open ? "Hide the canonical bytes" : "Show the canonical bytes";
        pre.hidden = !open;
      });
      rows.push(toggle, pre);
    }
    if (s.checks !== undefined) {
      rows.unshift(
        el(
          "div",
          { class: "checks" },
          s.checks.map((c, i) =>
            el("div", { class: "jcheck" }, [
              el("span", { class: "pass" }, ["✓"]),
              el("span", { class: "n" }, [String(i + 1).padStart(2, "0")]),
              // P1-8 (DCTA-192): a check's name and its code break only after "_" (app.css 72), never inside a word.
              el("span", { class: "nm" }, codeParts(c.name)),
              el("span", { class: "rc" }, codeParts(c.code)),
            ]),
          ),
        ),
      );
    }
    if (s.n === 9) {
      const verify = el("button", { class: "raw jrow open-chain", type: "button" }, [
        el("span", { class: "k" }, ["Verify"]),
        el("span", { class: "v" }, [el("span", { class: "mono" }, [`GET /v1/audit/verify?bundle_id=${bundleId}`])]),
        icon("i-chev-r", "chev"),
      ]);
      verify.addEventListener("click", onChain);
      rows.push(verify);
    }
    return el("div", { class: "jstage" }, [
      el("div", { class: "sh" }, [el("span", { class: "n" }, [String(s.n)]), s.title, el("span", { class: "mono" }, [s.meta])]),
      ...rows,
    ]);
  });
  // A pushed screen: the Judge tag is in its top bar, once (P2-8).
  return el("div", { class: "judge prv" }, [panel(["Last bundle", el("span", { class: "mono" }, [bundleId])], blocks, false), live]);
}

/**
 * DCTA-176 F49 (§2.5, mock-up 22-kyc.html `judge-proof`): the identity check's Proof — the turn refused before any question,
 * then the refresh signed — one panel each, in the order they happened, every stage numbered (a real sequence).
 */
export function identityProofView(panels: readonly Panel[]): HTMLElement {
  const live = announcer();
  const stage = (s: Stage): HTMLElement =>
    el("div", { class: "jstage" }, [
      el("div", { class: "sh" }, [el("span", { class: "n" }, [String(s.n)]), s.title, el("span", { class: s.tone === undefined ? "mono" : `${s.tone} mono` }, [s.meta])]),
      ...jrows(s.rows, live),
    ]);
  return el("div", { class: "judge prv" }, [
    ...panels.map((p) => panel([p.title, el("span", { class: "mono" }, [p.id])], [...jrows(p.head, live), ...p.stages.map(stage)])),
    live,
  ]);
}

// ------------------------------------------------------------------ attacks
/** "not run" = it could not run here (the signature attacks before a first signature): never a failure (QA-013). */
export type AttackVerdict = "blocked" | "differs" | "not run";

export interface AttackRow {
  readonly title: string;
  /** What was sent — or, for a row that did not run, why not (a sentence that wraps). */
  readonly input: string;
  readonly expected: string;
  readonly got: string;
  readonly verdict: AttackVerdict;
  readonly rows: readonly JRow[];
}

const PILL: Record<AttackVerdict, readonly [string, string]> = {
  blocked: ["pill", "✓ blocked"],
  differs: ["pill neg", "✗ differs"],
  "not run": ["pill idle", "not run yet"],
};

/**
 * DCTA-189 (QA DW-6): a reason code as the attack boxes draw it — its text, with a break opportunity (<wbr>) after each
 * "_", so a code too wide for its box breaks between its words ("INSTRUCTION_OVERRIDE_" / "BLOCKED"), never inside one;
 * vault.css keeps the box from breaking anywhere else. Text nodes and <wbr> only: the value is never read as markup.
 */
export function codeParts(code: string): (string | HTMLElement)[] {
  return code.split(/(?<=_)/).flatMap((part, i) => (i === 0 ? [part] : [el("wbr"), part]));
}

/**
 * DCTA-188: a diff key ("fixed:100000:SGD", "payee:pay_mum005", "none:<words>") as text that may break only after a ":"
 * or a "_" — a `<wbr>` there, text nodes everywhere else (never parsed: a `none:` key carries the customer's own words).
 */
export function keyParts(key: string): (string | HTMLElement)[] {
  return key.split(/(?<=[:_])/).flatMap((part, i) => (i === 0 ? [part] : [el("wbr"), part]));
}

const PASS_HEADS = ["Step", "Field", "Draft", "Validator", "Result"] as const;

/** Both passes as the diff compared them: one row group per pass, named by its header; the result in its own ink. */
function passTable(passes: readonly PassGroup[]): HTMLElement {
  return el("table", { class: "jpass" }, [
    el("thead", {}, [el("tr", {}, PASS_HEADS.map((h) => el("th", { scope: "col" }, [h])))]),
    ...passes.map((g) =>
      el("tbody", {}, [
        el("tr", { class: "grp" }, [el("th", { scope: "rowgroup", colspan: String(PASS_HEADS.length) }, [g.title])]),
        ...(g.count === undefined ? [] : [el("tr", {}, [el("td", { colspan: String(PASS_HEADS.length) }, [g.count])])]),
        ...g.rows.map((f) =>
          el("tr", {}, [
            el("td", {}, [f.step_id]),
            el("td", {}, keyParts(f.field)),
            el("td", { class: "mono" }, keyParts(f.drafter)),
            el("td", { class: "mono" }, keyParts(f.validator)),
            el("td", { class: f.result === "disagree" ? "fail" : f.result === "agree" ? "pass" : "" }, [f.result]),
          ]),
        ),
      ]),
    ),
  ]);
}

/**
 * DCTA-188 Change 3: judge mode's block under a freeze — the rule row and the re-check row (app/freeze-judge.ts), then
 * both passes. A `.judge` root: the one root the customer-copy audit exempts; nothing here is customer copy.
 */
export function freezeJudgeView(m: { readonly rows: readonly JRow[]; readonly passes: readonly PassGroup[] }): HTMLElement {
  const live = announcer();
  return el("div", { class: "judge jfreeze" }, [...jrows(m.rows, live), ...(m.passes.length === 0 ? [] : [passTable(m.passes)]), live]);
}

/** Beside a card or question the re-check cleared: its one line, the two-pass table folded under it. */
export function clearedJudgeView(line: string, passes: readonly PassGroup[]): HTMLElement {
  return el("details", { class: "judge jcleared" }, [el("summary", {}, [line]), ...(passes.length === 0 ? [] : [passTable(passes)])]);
}

/** Judge mode's Attacks row: "2 of 2 blocked" (green or red on what ran), "2 not run yet" neutral on its own line. */
function attackCount(a: { readonly blocked: number; readonly ran: number; readonly total: number }): HTMLElement {
  const notRun = a.total - a.ran;
  return el("span", {}, [
    el("span", { class: a.blocked === a.ran ? "pass" : "fail" }, [`${String(a.blocked)} of ${String(a.ran)} blocked`]),
    ...(notRun > 0 ? [el("small", {}, [`${String(notRun)} not run yet`])] : []),
  ]);
}

/**
 * QA D7: `error` — the run could not finish (the ledger unreadable, no answer, anything thrown): the panel says what stopped
 * and that nothing moved, and Run all four again is ready. It never stays on "running…".
 */
export function attacksView(results: readonly AttackRow[] | undefined, ledger: string | undefined, onRun: (done: () => void) => void, error?: string): HTMLElement {
  const live = announcer();
  const running = results === undefined && error === undefined;
  const run = el("button", { class: "raw btn ghost", type: "button", aria: { busy: running ? "true" : "false" } }, [
    running ? "Running the four attacks" : "Run all four again",
  ]);
  run.addEventListener("click", () => {
    if (run.getAttribute("aria-busy") === "true") return;
    run.setAttribute("aria-busy", "true");
    onRun(() => run.setAttribute("aria-busy", "false"));
  });
  const rows = (results ?? []).map((a) => {
    const [pillClass, pillText] = PILL[a.verdict];
    const ran = a.verdict !== "not run";
    const b = el("button", { class: "raw atk", type: "button", aria: { expanded: "false" } }, [
      el("span", { class: "t" }, [a.title, el("span", { class: pillClass }, [pillText])]),
      el("span", { class: ran ? "in" : "in why" }, [a.input]),
      el("span", { class: "dt" }, [
        el("span", { class: "cmp" }, [
          el("span", {}, [el("small", {}, ["Expected"]), el("code", {}, codeParts(a.expected))]),
          el("span", {}, [
            el("small", {}, ["Got"]),
            el("code", {}, codeParts(a.got)),
            ...(ran ? [el("span", { class: a.verdict === "blocked" ? "pass" : "fail" }, [a.verdict === "blocked" ? "✓" : "✗"])] : []),
          ]),
        ]),
        ...jrows(a.rows, live),
      ]),
    ]);
    b.dataset["expected"] = a.expected;
    b.dataset["got"] = a.got;
    b.addEventListener("click", () => b.setAttribute("aria-expanded", b.getAttribute("aria-expanded") === "true" ? "false" : "true"));
    return b;
  });
  const stopped = error === undefined ? [] : [el("div", { class: "jrow note", role: "alert" }, [el("span", { class: "v" }, [`${error} Nothing was signed and nothing moved. Run them again.`])])];
  return el("div", { class: "judge atv" }, [
    panel(
      error !== undefined
        ? ["Attacks", el("span", { class: "mono" }, ["could not finish"])]
        : results === undefined
          ? ["Attacks", el("span", { class: "mono" }, ["running…"])]
          : attackHead(results, ledger),
      [...stopped, ...rows, el("div", { class: "acts" }, [run])],
      false,
    ),
    live,
  ]);
}

/** "4 attacks · 4 blocked", or — when some could not run — "2 attacks run · 2 blocked · 2 not run yet" (QA-013). */
function attackHead(results: readonly AttackRow[], ledger: string | undefined): (Node | string)[] {
  const blocked = results.filter((r) => r.verdict === "blocked").length;
  const ran = results.filter((r) => r.verdict !== "not run").length;
  const notRun = results.length - ran;
  // One inline run, so the dots sit a word-space from each count (the head is a flex row with a gap).
  return [
    el("span", {}, [
      notRun === 0 ? `${String(ran)} attacks · ` : `${String(ran)} attacks run · `,
      el("span", { class: blocked === ran ? "pass" : "fail" }, [`${String(blocked)} blocked`]),
      ...(notRun > 0 ? [` · ${String(notRun)} not run yet`] : []),
    ]),
    el("span", { class: "mono" }, [ledger ?? ""]),
  ];
}

// -------------------------------------------------------------------- chain
export interface ChainHandlers {
  onVerify(done: () => void): void;
  onTamper(): void;
}

export function chainView(ok: boolean, rows: readonly JRow[], brokenAt: string | undefined, h: ChainHandlers): HTMLElement {
  const live = announcer();
  const again = el("button", { class: "raw btn ghost verify-again", type: "button", aria: { busy: "false" } }, ["Verify again"]);
  again.addEventListener("click", () => {
    if (again.getAttribute("aria-busy") === "true") return;
    again.setAttribute("aria-busy", "true");
    h.onVerify(() => again.setAttribute("aria-busy", "false"));
  });
  const acts = el("div", { class: "acts" });
  const idle = (): void => {
    if (!ok) {
      acts.replaceChildren(
        again,
        el("div", { class: "jrow note" }, [
          el("span", { class: "v" }, [
            "The edit and this check are records too. A clean chain needs a fresh demo database, not a reset — the reset is itself a record and deletes nothing.",
          ]),
        ]),
      );
      return;
    }
    const tamper = el("button", { class: "raw btn link tamper", type: "button" }, ["Edit a record on purpose · demo only"]);
    tamper.addEventListener("click", () =>
      ask(
        acts,
        "This edits the last payment's displayed record for real. The trail stays broken until a fresh demo database.",
        "Edit it",
        "Keep the trail",
        () => h.onTamper(),
        idle,
      ),
    );
    acts.replaceChildren(again, tamper);
  };
  idle();
  return el("div", { class: "judge chv" }, [
    ...(ok || brokenAt === undefined
      ? []
      : [
          el("div", { class: "jalert", role: "alert" }, [
            icon("i-alert"),
            el("div", {}, [
              el("b", {}, [`Broken at record ${brokenAt} · edited`]),
              `This trail can't be shown as evidence. The link to record ${brokenAt} does not match its bytes; everything after it is unverifiable.`,
            ]),
          ]),
        ]),
    panel(
      ["Chain ", el("span", { class: ok ? "pass" : "fail" }, [ok ? "verified" : "broken"]), el("span", { class: "mono" }, ["GET /v1/audit/verify"])],
      [...jrows(rows, live), acts],
      false,
    ),
    live,
  ]);
}
